import CryptoJS from 'crypto-js';
import { ethers } from 'ethers';

// ==================== ENCRYPTION & SECURITY CORE ====================

// Secure key derivation using PBKDF2 with high iteration count
const deriveKeyFromPassword = (password, salt) => {
  console.log('🔐 Deriving encryption key from password...');
  const key = CryptoJS.PBKDF2(password, salt, {
    keySize: 256 / 32, // 256-bit key for AES-256
    iterations: 100000, // High iteration count for brute-force protection
    hasher: CryptoJS.algo.SHA256
  });
  return key.toString();
};

// Generate cryptographically secure random salt
const generateSalt = () => {
  const salt = CryptoJS.lib.WordArray.random(128 / 8).toString();
  console.log('🔐 Generated new salt:', salt.slice(0, 16) + '...');
  return salt;
};

// Enhanced secure storage with versioning and validation
export const SecureStorage = {
  // Current version for data migration support
  VERSION: '1.1',

  // Encrypt data with user-provided password and metadata
  encryptData: (data, password) => {
    try {
      console.log('🔐 Starting data encryption...');

      if (!password || password.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      const salt = generateSalt();
      const encryptionKey = deriveKeyFromPassword(password, salt);

      const encryptedData = CryptoJS.AES.encrypt(
        JSON.stringify({
          data: data,
          timestamp: new Date().toISOString(),
          version: SecureStorage.VERSION
        }),
        encryptionKey
      ).toString();

      const encryptedPackage = {
        encrypted: encryptedData,
        salt: salt,
        version: SecureStorage.VERSION,
        timestamp: new Date().toISOString(),
        checksum: CryptoJS.SHA256(encryptedData).toString()
      };

      console.log('✅ Data encrypted successfully');
      return encryptedPackage;
    } catch (error) {
      console.error('❌ Encryption failed:', error);
      throw new Error('Failed to encrypt wallet data: ' + error.message);
    }
  },

  // Decrypt data with user-provided password and integrity checks
  decryptData: (encryptedPackage, password) => {
    try {
      console.log('🔐 Starting data decryption...');

      const { encrypted, salt, checksum } = encryptedPackage;

      // Validate package integrity
      if (!encrypted || !salt) {
        throw new Error('Invalid encrypted package structure');
      }

      // Verify checksum if available
      if (checksum && CryptoJS.SHA256(encrypted).toString() !== checksum) {
        throw new Error('Data integrity check failed - package may be corrupted');
      }

      const encryptionKey = deriveKeyFromPassword(password, salt);
      const bytes = CryptoJS.AES.decrypt(encrypted, encryptionKey);
      const decryptedJson = bytes.toString(CryptoJS.enc.Utf8);

      if (!decryptedJson) {
        throw new Error('Decryption failed - incorrect password or corrupted data');
      }

      const decryptedData = JSON.parse(decryptedJson);

      // ✅ FIXED: Handle version mismatch gracefully
      if (decryptedData.version !== SecureStorage.VERSION) {
        console.warn(`⚠️ Version mismatch: stored ${decryptedData.version}, current ${SecureStorage.VERSION}`);
        // For undefined versions, assume it's the data directly (old format)
        if (decryptedData.version === undefined) {
          console.log('🔄 Handling legacy data format (version undefined)');
          return decryptedData; // Return the data directly for legacy format
        }
        // Future: Add proper migration logic here for other version changes
      }

      console.log('✅ Data decrypted successfully');
      return decryptedData.data;
    } catch (error) {
      console.error('❌ Decryption failed:', error);

      if (error.message.includes('incorrect password') || error.message.includes('Decryption failed')) {
        throw new Error('Incorrect password. Please try again.');
      } else if (error.message.includes('corrupted')) {
        throw new Error('Wallet data appears to be corrupted.');
      } else {
        throw new Error('Failed to decrypt wallet data: ' + error.message);
      }
    }
  },

  // Save wallets with password protection and validation
  saveAccounts: async (wallets, password) => {
    try {
      console.log('💾 Saving accounts to secure storage...');

      if (!Array.isArray(wallets)) {
        throw new Error('Invalid wallets data format');
      }

      if (!password || password.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      // Validate each wallet structure
      wallets.forEach((wallet, index) => {
        if (!wallet.address || !wallet.privateKey) {
          throw new Error(`Invalid wallet structure at index ${index}`);
        }

        if (!wallet.address.startsWith('0x') || wallet.address.length !== 42) {
          throw new Error(`Invalid wallet address at index ${index}`);
        }
      });

      const encryptedPackage = SecureStorage.encryptData(wallets, password);
      localStorage.setItem('zwallet_accounts', JSON.stringify(encryptedPackage));

      console.log(`✅ ${wallets.length} wallet(s) saved successfully`);
      return true;
    } catch (error) {
      console.error('❌ Failed to save wallets:', error);
      throw error;
    }
  },

  // Load wallets with password verification and error handling
  loadAccounts: async (password) => {
    try {
      console.log('💾 Loading accounts from secure storage...');

      const stored = localStorage.getItem('zwallet_accounts');
      if (!stored) {
        console.log('ℹ️ No stored wallets found');
        return [];
      }

      const encryptedPackage = JSON.parse(stored);
      const wallets = await SecureStorage.decryptData(encryptedPackage, password);

      // ✅ FIXED: Handle case where wallets might be undefined
      if (!wallets || !Array.isArray(wallets)) {
        console.warn('⚠️ No valid wallets array found after decryption');
        return [];
      }

      console.log(`✅ ${wallets.length} wallet(s) loaded successfully`);
      return wallets;
    } catch (error) {
      console.error('❌ Failed to load wallets:', error);
      throw error;
    }
  },

  // FIXED: Check if wallets exist - Check multiple storage locations
  hasAccounts: () => {
    console.log('🔍 Checking for wallet existence...');
    
    // Check multiple possible storage locations
    const storageKeys = [
      'zwallet_accounts',
      'zwallet_created',
      'zwallet:accounts',
      'zwallet_accounts_encrypted',
      'accounts'
    ];
    
    for (const key of storageKeys) {
      const value = localStorage.getItem(key);
      if (value) {
        console.log(`✅ Found wallet data in key: ${key}`);
        return true;
      }
    }
    
    // Also check if there's any session data
    const session = localStorage.getItem('zwallet_session');
    if (session) {
      console.log('✅ Found wallet session data');
      return true;
    }
    
    console.log('❌ No wallet data found in any storage location');
    return false;
  },

  // Clear all wallet data securely
  clearAccounts: () => {
    try {
      console.log('🗑️ Clearing all wallet data...');
      localStorage.removeItem('zwallet_accounts');
      localStorage.removeItem('zwallet_passwordHash');
      sessionStorage.removeItem('zwallet_session');
      console.log('✅ All wallet data cleared successfully');
    } catch (error) {
      console.error('❌ Failed to clear wallet data:', error);
      throw error;
    }
  },

  // Get storage statistics
  getStorageInfo: () => {
    try {
      const stored = localStorage.getItem('zwallet_accounts');
      if (!stored) return null;

      const encryptedPackage = JSON.parse(stored);
      return {
        version: encryptedPackage.version,
        timestamp: encryptedPackage.timestamp,
        size: stored.length,
        hasChecksum: !!encryptedPackage.checksum
      };
    } catch (error) {
      console.error('Failed to get storage info:', error);
      return null;
    }
  }
};

// ==================== PASSWORD & SESSION MANAGEMENT ====================

export const PasswordManager = {
  // Password strength validation
  validatePasswordStrength: (password) => {
    const requirements = {
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumbers: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };

    const strength = Object.values(requirements).filter(Boolean).length;
    const isStrong = strength >= 4; // At least 4 out of 5 requirements

    return {
      requirements,
      strength,
      isStrong,
      score: Math.round((strength / 5) * 100)
    };
  },

  // Create a password hash for verification (without storing the actual password)
  createPasswordHash: (password) => {
    const salt = CryptoJS.lib.WordArray.random(128 / 8).toString();
    const hash = CryptoJS.PBKDF2(password, salt, {
      keySize: 256 / 32,
      iterations: 10000,
      hasher: CryptoJS.algo.SHA256
    }).toString();
    return { hash, salt };
  },

  // Verify password without decrypting all data
  verifyPassword: (password, storedHash, storedSalt) => {
    const testHash = CryptoJS.PBKDF2(password, storedSalt, {
      keySize: 256 / 32,
      iterations: 10000,
      hasher: CryptoJS.algo.SHA256
    }).toString();
    return testHash === storedHash;
  },

  // Store session (in memory only, not localStorage) with expiration
  createSession: (password, duration = 30 * 60 * 1000) => { // 30 minutes default
    const session = {
      password: password,
      expires: Date.now() + duration,
      created: new Date().toISOString()
    };

    sessionStorage.setItem('zwallet_session', JSON.stringify(session));
    console.log('🔐 Session created, expires in:', duration / 60000, 'minutes');
    return session;
  },

  // Get current session with automatic expiry check
  getSession: () => {
    try {
      const sessionStr = sessionStorage.getItem('zwallet_session');
      if (!sessionStr) {
        console.log('🔐 No active session found');
        return null;
      }

      const session = JSON.parse(sessionStr);

      if (session.expires > Date.now()) {
        console.log('🔐 Valid session found');
        return session;
      } else {
        console.log('🔐 Session expired');
        PasswordManager.clearSession();
        return null;
      }
    } catch (error) {
      console.error('❌ Error reading session:', error);
      PasswordManager.clearSession();
      return null;
    }
  },

  // Extend session duration
  extendSession: (additionalTime = 30 * 60 * 1000) => {
    const session = PasswordManager.getSession();
    if (session) {
      session.expires = Date.now() + additionalTime;
      sessionStorage.setItem('zwallet_session', JSON.stringify(session));
      console.log('🔐 Session extended');
      return session;
    }
    return null;
  },

  // Clear session securely
  clearSession: () => {
    sessionStorage.removeItem('zwallet_session');
    console.log('🔐 Session cleared');
  },

  // Get session info
  getSessionInfo: () => {
    const session = PasswordManager.getSession();
    if (!session) return null;

    return {
      created: session.created,
      expires: new Date(session.expires),
      timeRemaining: session.expires - Date.now(),
      isValid: session.expires > Date.now()
    };
  },

  // 🔥 ADDED: Save session for auto-login
  saveSession: (password) => {
    try {
      console.log('💾 Saving session for auto-login...');
      const session = PasswordManager.createSession(password);
      console.log('✅ Session saved successfully');
      return session;
    } catch (error) {
      console.error('❌ Failed to save session:', error);
      return null;
    }
  }
};

// ==================== LOCAL WALLET MANAGEMENT ====================

export const LocalWalletManager = {
  // Create new wallet with comprehensive validation
  createNewWallet: async (password, localWallets, saveLocalWallets, setSeedPhrase, setShowSeedPhrase, setError) => {
    try {
      console.log('🆕 Creating new wallet...');

      // Validate password strength
      const passwordStrength = PasswordManager.validatePasswordStrength(password);
      if (!passwordStrength.isStrong) {
        throw new Error('Password is too weak. Please use a stronger password with at least 8 characters including uppercase, lowercase, numbers, and special characters.');
      }

      const wallet = ethers.Wallet.createRandom();

      // Validate generated wallet
      if (!wallet.address || !wallet.privateKey || !wallet.mnemonic) {
        throw new Error('Failed to generate valid wallet');
      }

      const newWallet = {
        address: wallet.address,
        privateKey: wallet.privateKey,
        mnemonic: wallet.mnemonic.phrase,
        publicKey: wallet.publicKey,
        createdAt: new Date().toISOString(),
        name: `Zwallet Account ${localWallets.length + 1}`,
        type: 'local',
        derivationPath: wallet.mnemonic.path || "m/44'/60'/0'/0/0"
      };

      console.log('✅ New wallet created:', wallet.address);

      // Show seed phrase to user
      setSeedPhrase(wallet.mnemonic.phrase);
      setShowSeedPhrase(true);

      // Save to secure storage
      const updatedWallets = [...localWallets, newWallet];
      await saveLocalWallets(updatedWallets, password);

      return newWallet;
    } catch (error) {
      console.error('❌ Failed to create wallet:', error);
      setError('Failed to create wallet: ' + error.message);
      return null;
    }
  },

  // Import wallet from seed phrase with validation
  importWalletFromSeed: async (importSeedPhrase, password, localWallets, saveLocalWallets, setActiveLocalWallet, setShowImportWalletModal, setImportSeedPhrase, setError) => {
    try {
      console.log('📥 Importing wallet from seed phrase...');

      if (!importSeedPhrase.trim()) {
        throw new Error('Please enter a seed phrase');
      }

      // Basic seed phrase validation
      const wordCount = importSeedPhrase.trim().split(/\s+/).length;
      if (wordCount !== 12 && wordCount !== 24) {
        throw new Error('Seed phrase must be 12 or 24 words');
      }

      const wallet = ethers.Wallet.fromPhrase(importSeedPhrase.trim());

      // Check for duplicates
      const isDuplicate = localWallets.some(w =>
        w.address.toLowerCase() === wallet.address.toLowerCase()
      );

      if (isDuplicate) {
        throw new Error('This wallet is already imported');
      }

      const importedWallet = {
        address: wallet.address,
        privateKey: wallet.privateKey,
        mnemonic: wallet.mnemonic.phrase,
        publicKey: wallet.publicKey,
        createdAt: new Date().toISOString(),
        name: `Imported Account ${localWallets.length + 1}`,
        type: 'local',
        derivationPath: wallet.mnemonic.path || "m/44'/60'/0'/0/0",
        imported: true
      };

      const updatedWallets = [...localWallets, importedWallet];
      await saveLocalWallets(updatedWallets, password);

      setActiveLocalWallet(importedWallet);
      setShowImportWalletModal(false);
      setImportSeedPhrase('');

      console.log('✅ Wallet imported successfully:', wallet.address);
      return importedWallet;
    } catch (error) {
      console.error('❌ Failed to import wallet:', error);

      if (error.message.includes('invalid mnemonic') || error.message.includes('invalid phrase')) {
        setError('Invalid seed phrase. Please check the words and try again.');
      } else if (error.message.includes('already imported')) {
        setError('This wallet is already in your account list.');
      } else {
        setError('Failed to import wallet: ' + error.message);
      }
      return null;
    }
  },

  // Connect to local wallet with provider setup
  connectLocalWallet: async (wallet, setProvider, setSigner, setAccount, setActiveLocalWallet, setWalletType, updateWalletData, setIsConnected, setError, setShowLocalWalletModal, setLoading) => {
    try {
      console.log('🔗 Connecting local wallet:', wallet.address);
      setLoading(true);

      // Use FREE Ankr endpoint (no API key needed)
      const jsonRpcProvider = new ethers.JsonRpcProvider(`https://rpc.ankr.com/eth`);
      const walletWithProvider = new ethers.Wallet(wallet.privateKey, jsonRpcProvider);

      // Verify connection by getting balance
      const balance = await jsonRpcProvider.getBalance(wallet.address);
      console.log('💰 Wallet balance:', ethers.formatEther(balance));

      setProvider(jsonRpcProvider);
      setSigner(walletWithProvider);
      setAccount(wallet.address);
      setActiveLocalWallet(wallet);
      setWalletType('local');

      await updateWalletData(jsonRpcProvider, wallet.address);
      setIsConnected(true);
      setError('');
      setShowLocalWalletModal(false);

      console.log('✅ Local wallet connected successfully');
    } catch (error) {
      console.error('❌ Failed to connect local wallet:', error);
      setError('Failed to connect wallet: ' + error.message);
    } finally {
      setLoading(false);
    }
  },

  // Validate wallet address format
  validateAddress: (address) => {
    try {
      return ethers.isAddress(address);
    } catch (error) {
      return false;
    }
  },

  // Get wallet info without private key exposure
  getWalletInfo: (wallet) => {
    return {
      address: wallet.address,
      name: wallet.name,
      type: wallet.type,
      createdAt: wallet.createdAt,
      imported: wallet.imported || false,
      balance: wallet.balance || '0'
    };
  }
};

// ==================== SECURITY UTILITIES ====================

export const SecurityUtils = {
  // Generate secure random bytes
  generateRandomBytes: (length = 32) => {
    return CryptoJS.lib.WordArray.random(length).toString();
  },

  // Create backup code for emergency recovery
  generateBackupCode: () => {
    const code = CryptoJS.lib.WordArray.random(16).toString();
    const timestamp = new Date().toISOString();

    return {
      code: code,
      timestamp: timestamp,
      hash: CryptoJS.SHA256(code + timestamp).toString()
    };
  },

  // Validate backup code
  validateBackupCode: (code, backupData) => {
    const testHash = CryptoJS.SHA256(code + backupData.timestamp).toString();
    return testHash === backupData.hash;
  },

  // Security audit log
  auditLog: {
    log: (action, details = {}) => {
      const logEntry = {
        action,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        details
      };

      console.log('🔍 Security Audit:', logEntry);

      // In production, this would send to a secure logging service
      const auditLogs = JSON.parse(localStorage.getItem('zwallet_auditLog') || '[]');
      auditLogs.push(logEntry);

      // Keep only last 1000 entries
      if (auditLogs.length > 1000) {
        auditLogs.splice(0, auditLogs.length - 1000);
      }

      localStorage.setItem('zwallet_auditLog', JSON.stringify(auditLogs));
    },

    getLogs: (limit = 50) => {
      const logs = JSON.parse(localStorage.getItem('zwallet_auditLog') || '[]');
      return logs.slice(-limit);
    },

    clearLogs: () => {
      localStorage.removeItem('zwallet_auditLog');
    }
  }
};

// No default export - use named imports
// Example: import { SecureStorage, PasswordManager, LocalWalletManager } from './ZwalletSecurity';
